// ============================================================
// PersianCalendar.jsx - تقویم آموزشی شمسی برای کودکان
// ============================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-jalaali';
import { useAuth } from '../../context/AuthContext.jsx';
import { useTeam } from '../../team/useTeam';
import { useExams } from '../Exams/ExamContext.jsx';
import './PersianCalendar.css';

// ============================================================
// تبدیل اعداد انگلیسی به فارسی
// ============================================================
const toPersianDigits = (num) => {
  const id = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return num.toString().replace(/[0-9]/g, (w) => id[+w]);
};

// ============================================================
// تبدیل اسم ماه انگلیسی به فارسی (امن‌ترین روش)
// ============================================================
const toPersianMonth = (monthName) => {
  const months = {
    'Farvardin': 'فروردین',
    'Ordibehesht': 'اردیبهشت',
    'Khordaad': 'خرداد',
    'Tir': 'تیر',
    'Amordaad': 'مرداد',
    'Shahrivar': 'شهریور',
    'Mehr': 'مهر',
    'Aabaan': 'آبان',
    'Aazar': 'آذر',
    'Dey': 'دی',
    'Bahman': 'بهمن',
    'Esfand': 'اسفند'
  };
  return months[monthName] || monthName;
};

// ============================================================
// کامپوننت اصلی تقویم
// ============================================================
const PersianCalendar = () => {
  const navigate = useNavigate();

  // ===== اطلاعات کاربر =====
  // ✅ از AuthContext می‌خونیم — نه از localStorage
  const { user } = useAuth();
  const currentUserId = user?.id;
  const currentUserRole = user?.role;
  const isTeacher = currentUserRole === 'teacher';
  const isStudent = currentUserRole === 'student';

const [isDark, setIsDark] = useState(() => {
  try {
    return localStorage.getItem('navbar2-drawer-dark') === '1';
  } catch {
    return false;
  }
});

  // ===== اعمال تم روی صفحه (هماهنگ با بقیه صفحات) =====
// ذخیره در کلید مشترک Navbar2 + اعمال data-theme روی html (برای CSS این صفحه)
useEffect(() => {
  try {
    localStorage.setItem('navbar2-drawer-dark', isDark ? '1' : '0');
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : '');
  } catch {}
  return () => {
    document.documentElement.setAttribute('data-theme', '');
  };
}, [isDark]);

// گوش دادن به تغییرات Navbar2 (storage بین تب‌ها + polling داخل همون تب)
useEffect(() => {
  const handleStorage = (e) => {
    if (e.key === 'navbar2-drawer-dark') {
      setIsDark(e.newValue === '1');
    }
  };
  window.addEventListener('storage', handleStorage);

  const interval = setInterval(() => {
    try {
      const current = localStorage.getItem('navbar2-drawer-dark') === '1';
      setIsDark((prev) => (prev !== current ? current : prev));
    } catch {}
  }, 500);

  return () => {
    window.removeEventListener('storage', handleStorage);
    clearInterval(interval);
  };
}, []);

  // ===== هوک‌های تیم و آزمون =====
  const {
    team,
    members,
    tasks,
    channelMessages,
    loading: teamLoading,
  } = useTeam(currentUserId, currentUserRole);

  const {
    exams,
    getExamsForStudent,
    getStudentResult,
    isExamOpenForEntry,
  } = useExams();

  // ===== استیت‌های تقویم =====
  const [currentDate, setCurrentDate] = useState(moment());
  const [today] = useState(moment());
  const [selectedDate, setSelectedDate] = useState(null);

  // ===== استیت برای نمایش جدول‌ها =====
  const [showTasks, setShowTasks] = useState(false);
  const [showMissions, setShowMissions] = useState(false);

  // ===== تغییر ماه =====
  const changeMonth = (delta) => {
    setCurrentDate(currentDate.clone().add(delta, 'jMonth'));
  };

  // ===== بازگشت به امروز =====
  const goToToday = () => {
    setCurrentDate(moment());
    setSelectedDate(moment());
  };

  // ===== تولید روزهای ماه =====
  const generateDays = useCallback(() => {
    const startOfMonth = currentDate.clone().startOf('jMonth');
    const endOfMonth = currentDate.clone().endOf('jMonth');
    const days = [];
    let day = startOfMonth.clone();

    const startDayOfWeek = startOfMonth.day();
    const offset = (startDayOfWeek + 1) % 7;
    for (let i = 0; i < offset; i++) {
      days.push(null);
    }

    while (day <= endOfMonth) {
      days.push(day.clone());
      day.add(1, 'day');
    }

    return days;
  }, [currentDate]);

  const days = generateDays();
  const weekDays = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'];

  // ===== محاسبه ماموریت‌ها (برای دانش‌آموز) =====
  const missions = useMemo(() => {
    if (!isStudent || !team?.id || !currentUserId) return [];

    const teamExams = getExamsForStudent(team.id, currentUserId);

    return teamExams.map((exam) => {
      const result = getStudentResult(exam.id, currentUserId);
      const isOpen = isExamOpenForEntry(exam);
      const status = exam.statusType || 'pending';
      let statusLabel = '';

      if (status === 'active' && isOpen) statusLabel = 'فعال';
      else if (status === 'pending') statusLabel = 'در انتظار';
      else if (status === 'closed') statusLabel = 'بسته شده';
      else if (status === 'correction') statusLabel = 'نیاز به تصحیح';
      else if (!isOpen && status === 'active') statusLabel = 'شروع نشده';

      const hasParticipated = !!result;
      const isCompleted = result && result.isEssayGraded !== false;
      const maxScore = exam.questions?.reduce((sum, q) => sum + Number(q.score || 1), 0) || 0;
      const userScore = result ? Number(result.score || 0) : 0;
      const percent = maxScore > 0 ? Math.round((userScore / maxScore) * 100) : 0;

      const examDate = exam.startDate ? moment(exam.startDate, 'YYYY-MM-DD') : null;

      return {
        id: exam.id,
        title: exam.title,
        book: exam.book || 'بدون کتاب',
        status,
        statusLabel,
        isOpen,
        hasParticipated,
        isCompleted,
        needsCorrection: status === 'correction',
        isPassed: maxScore > 0 ? userScore >= (maxScore * 0.5) : false,
        percent,
        score: `${userScore}/${maxScore}`,
        reward: `${maxScore} ⭐`,
        time: exam.duration ? `${exam.duration} دقیقه` : '--',
        date: examDate,
        isEssay: (exam.questions || []).some(q => q.type === 'essay' || q.type === 'file'),
        isFullyGraded: result?.isEssayGraded !== false,
      };
    });
  }, [isStudent, team, currentUserId, getExamsForStudent, getStudentResult, isExamOpenForEntry]);

  // ===== وظایف (از useTeam) =====
  const allTasks = tasks || [];

  // ===== توابع فیلتر بر اساس تاریخ انتخاب‌شده =====
  const getTasksForDate = useCallback((dateMoment) => {
    if (!dateMoment) return [];
    const dateStr = dateMoment.format('jYYYY-jMM-jDD');
    return allTasks.filter(task => {
      if (!task.dueDate) return false;
      const taskDate = moment(task.dueDate, 'YYYY-MM-DD');
      return taskDate.format('jYYYY-jMM-jDD') === dateStr;
    });
  }, [allTasks]);

  const getMissionsForDate = useCallback((dateMoment) => {
    if (!dateMoment || !isStudent) return [];
    const dateStr = dateMoment.format('jYYYY-jMM-jDD');
    return missions.filter(mission => {
      if (!mission.date) return false;
      return mission.date.format('jYYYY-jMM-jDD') === dateStr;
    });
  }, [missions, isStudent]);

  // ===== بررسی اینکه روز قابل کلیک هست یا نه =====
  const isDayClickable = useCallback((day) => {
    if (!day) return false;
    if (day.isAfter(today, 'day')) return true;
    if (day.isSame(today, 'day')) return true;
    return false;
  }, [today]);

  // ===== کلیک روی روز =====
  const handleDayClick = useCallback((day) => {
    if (!isDayClickable(day)) return;

    setSelectedDate(day);
    setShowTasks(true);
    setShowMissions(true);

    if (!isStudent) {
      setShowMissions(false);
    }
  }, [isDayClickable, isStudent]);

  // ===== بستن جدول‌ها =====
  const closeTables = () => {
    setSelectedDate(null);
    setShowTasks(false);
    setShowMissions(false);
  };

  // ===== اگر روز انتخاب نشده، هیچ جدولی نمایش داده نشه =====
  const selectedTasks = selectedDate ? getTasksForDate(selectedDate) : [];
  const selectedMissions = selectedDate ? getMissionsForDate(selectedDate) : [];

  // ===== فرمت نمایش تاریخ انتخاب‌شده با اعداد و ماه فارسی =====
  const formatSelectedDate = (date) => {
    if (!date) return '';
    const day = toPersianDigits(date.format('jD'));
    const month = toPersianMonth(date.format('jMMMM'));
    const year = toPersianDigits(date.format('jYYYY'));
    return `${day} ${month} ${year}`;
  };

  // ===== بررسی وجود تکلیف در روز =====
  const hasTaskOnDay = useCallback((day) => {
    if (!day) return false;
    const tasksForDay = getTasksForDate(day);
    return tasksForDay.length > 0;
  }, [getTasksForDate]);

  const hasMissionOnDay = useCallback((day) => {
    if (!day || !isStudent) return false;
    const missionsForDay = getMissionsForDate(day);
    return missionsForDay.length > 0;
  }, [getMissionsForDate, isStudent]);

  // ===== وضعیت روز (برای نمایش آیکون) =====
  const getDayStatus = useCallback((day) => {
    if (!day) return null;
    const hasTask = hasTaskOnDay(day);
    const hasMission = hasMissionOnDay(day);
    if (hasTask && hasMission) return 'both';
    if (hasTask) return 'task';
    if (hasMission) return 'mission';
    return null;
  }, [hasTaskOnDay, hasMissionOnDay]);

  // ============================================================
  // ===== رندر اصلی =====
  // ============================================================

  if (teamLoading) {
    return (
      <div className={`calendar-page ${isDark ? 'dark-theme' : ''}`}>
        <div className="calendar-loading">
          <div className="calendar-loading__spinner"></div>
          <span>در حال بارگذاری تقویم...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`calendar-page ${isDark ? 'dark-theme' : ''}`}>
      {/* ===== پس‌زمینه شاد ===== */}
      <div className="calendar-bg">
        <div className="calendar-bg__blob calendar-bg__blob--1"></div>
        <div className="calendar-bg__blob calendar-bg__blob--2"></div>
        <div className="calendar-bg__blob calendar-bg__blob--3"></div>
        <div className="calendar-bg__blob calendar-bg__blob--4"></div>
        <div className="calendar-bg__blob calendar-bg__blob--5"></div>
      </div>

      <div className="calendar-container">
        {/* ===== هدر صفحه با دکمه بازگشت ===== */}
                <header className="calendar-header">
          <button className="calendar-header__back" onClick={() => navigate(-1)}>
            ✕
          </button>
          
          {/* ✅ دکمه سوییچ تم 
          <button
            className="calendar-header__theme"
            onClick={() => setIsDark(!isDark)}
            title={isDark ? 'تم روشن' : 'تم تاریک'}
          >
            {isDark ? '☀️' : '🌙'}
          </button>*/}
          
          <div className="calendar-header__content">
            <h1 className="calendar-header__title">
              <span className="calendar-header__icon"></span>
              تقویم آموزشی
            </h1>
            <p className="calendar-header__subtitle">
              {isTeacher ? '👨‍🏫 مدیریت تکالیف و وظایف' : '🧑‍🎓 ماموریت‌ها و وظایف روزانه'}
            </p>
          </div>
        </header>

        {/* ===== کارت تقویم ===== */}
        <div className="calendar-card">
          {/* نوار ماه - با اسم فارسی */}
          <div className="calendar-nav">
            <button className="calendar-nav__btn" onClick={() => changeMonth(-1)}>
              ▶
            </button>
            <h2 className="calendar-nav__month">
              {toPersianDigits(
                toPersianMonth(currentDate.format('jMMMM')) + ' ' + currentDate.format('jYYYY')
              )}
            </h2>
            <button className="calendar-nav__btn" onClick={() => changeMonth(1)}>
              ◀
            </button>
            <button className="calendar-nav__today" onClick={goToToday}>
              📍 امروز
            </button>
          </div>

          {/* روزهای هفته */}
          <div className="calendar-weekdays">
            {weekDays.map((day) => (
              <span key={day} className="calendar-weekday">
                {day}
              </span>
            ))}
          </div>

          {/* شبکه روزها */}
          <div className="calendar-grid">
            {days.map((day, index) => {
              if (!day) {
                return <div key={`empty-${index}`} className="calendar-day calendar-day--empty"></div>;
              }

              const isToday = day.isSame(today, 'day');
              const isPast = day.isBefore(today, 'day');
              const isFuture = day.isAfter(today, 'day');
              const isSelected = selectedDate && day.isSame(selectedDate, 'day');
              const clickable = isDayClickable(day);
              const status = getDayStatus(day);

              let className = 'calendar-day';
              if (isToday) className += ' calendar-day--today';
              if (isPast) className += ' calendar-day--past';
              if (isFuture) className += ' calendar-day--future';
              if (isSelected) className += ' calendar-day--selected';
              if (!clickable) className += ' calendar-day--disabled';
              if (status === 'task') className += ' calendar-day--has-task';
              if (status === 'mission') className += ' calendar-day--has-mission';
              if (status === 'both') className += ' calendar-day--has-both';

              return (
                <div
                  key={day.format('jYYYY-jMM-jDD')}
                  className={className}
                  onClick={() => clickable && handleDayClick(day)}
                >
                  <span className="calendar-day__number">
                    {toPersianDigits(day.format('jD'))}
                  </span>
                  {status === 'task' && <span className="calendar-day__badge">📋</span>}
                  {status === 'mission' && <span className="calendar-day__badge">📝</span>}
                  {status === 'both' && <span className="calendar-day__badge">⭐</span>}
                </div>
              );
            })}
          </div>

          {/* افسانه */}
          <div className="calendar-legend">
            <span className="calendar-legend__item">
              <span className="calendar-legend__dot calendar-legend__dot--today"></span> امروز
            </span>
            <span className="calendar-legend__item">
              <span className="calendar-legend__dot calendar-legend__dot--past"></span> گذشته
            </span>
            <span className="calendar-legend__item">
              <span className="calendar-legend__dot calendar-legend__dot--future"></span> آینده
            </span>
            <span className="calendar-legend__item">
              <span className="calendar-legend__dot calendar-legend__dot--task"></span> وظیفه
            </span>
            {isStudent && (
              <span className="calendar-legend__item">
                <span className="calendar-legend__dot calendar-legend__dot--mission"></span> ماموریت
              </span>
            )}
          </div>
        </div>

        {/* ===== بخش جدول‌های پایین ===== */}
        {selectedDate && (showTasks || showMissions) && (
          <div className="calendar-tables">
            <button className="calendar-tables__close" onClick={closeTables}>
              ✕
            </button>

            {/* عنوان روز انتخاب‌شده */}
            <h3 className="calendar-tables__date-title">
              📌 {formatSelectedDate(selectedDate)}
            </h3>

            <div className="calendar-tables__grid">
              {/* ===== جدول وظایف ===== */}
              {showTasks && (
                <div className="calendar-table calendar-table--tasks">
                  <div className="calendar-table__header">
                    <span className="calendar-table__icon">📋</span>
                    <h4 className="calendar-table__title">وظایف</h4>
                    <span className="calendar-table__count">{toPersianDigits(selectedTasks.length)}</span>
                  </div>
                  <div className="calendar-table__body">
                    {selectedTasks.length > 0 ? (
                      selectedTasks.map((task) => {
                        const priorityColors = {
                          high: '🔴',
                          medium: '🟡',
                          low: '🟢',
                        };
                        return (
                          <div key={task.id} className="calendar-table__row">
                            <span className="calendar-table__task-status">
                              {task.completed ? '✅' : '⏳'}
                            </span>
                            <span className={`calendar-table__task-title ${task.completed ? 'done' : ''}`}>
                              {priorityColors[task.priority || 'medium']} {task.title}
                            </span>
                            {task.dueDate && (
                              <span className="calendar-table__task-date">
                                {toPersianDigits(
                                  moment(task.dueDate).format('jD') + ' ' + 
                                  toPersianMonth(moment(task.dueDate).format('jMMMM'))
                                )}
                              </span>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="calendar-table__empty">
                        <span className="calendar-table__empty-icon">🎉</span>
                        <p>هیچ وظیفه‌ای برای این روز وجود ندارد</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ===== جدول ماموریت‌ها ===== */}
              {showMissions && isStudent && (
                <div className="calendar-table calendar-table--missions">
                  <div className="calendar-table__header">
                    <span className="calendar-table__icon">📝</span>
                    <h4 className="calendar-table__title">ماموریت‌ها</h4>
                    <span className="calendar-table__count">{toPersianDigits(selectedMissions.length)}</span>
                  </div>
                  <div className="calendar-table__body">
                    {selectedMissions.length > 0 ? (
                      selectedMissions.map((mission) => (
                        <div key={mission.id} className="calendar-table__row">
                          <span className="calendar-table__mission-status">
                            {mission.needsCorrection ? '⏳' :
                             mission.hasParticipated && mission.isCompleted ? '✅' :
                             mission.isOpen ? '🚀' : '🔒'}
                          </span>
                          <span className="calendar-table__mission-title">
                            {mission.title}
                          </span>
                          <span className={`calendar-table__mission-score ${mission.isPassed ? 'pass' : 'fail'}`}>
                            {mission.hasParticipated && !mission.needsCorrection
                              ? `${toPersianDigits(mission.percent)}%`
                              : mission.needsCorrection
                              ? 'در انتظار تصحیح'
                              : mission.reward}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="calendar-table__empty">
                        <span className="calendar-table__empty-icon">📭</span>
                        <p>هیچ ماموریتی برای این روز وجود ندارد</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== پیام در صورت عدم انتخاب روز ===== */}
        {!selectedDate && (
          <div className="calendar-hint">
            <span className="calendar-hint__icon">👆</span>
            <p className="calendar-hint__text">
              روی یک روز <strong>آینده</strong> یا <strong>امروز</strong> کلیک کنید تا تکالیف و ماموریت‌های آن روز را ببینید.
            </p>
            <p className="calendar-hint__sub">
              روزهای گذشته به رنگ مات و غیرقابل کلیک هستند.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersianCalendar;